import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getUserId } from "@/lib/session";

const AppCtx = createContext(null);

export function AppProvider({ children }) {
  const [uid] = useState(() => getUserId());

  const [a11yLarge, setA11yLarge] = useState(() => localStorage.getItem("lg.a11y.large") === "1");
  const [a11yContrast, setA11yContrast] = useState(() => localStorage.getItem("lg.a11y.contrast") === "1");
  const [reduceMotion, setReduceMotion] = useState(() => localStorage.getItem("lg.a11y.rm") === "1");
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("lg.a11y.voice") === "1");
  const [language, setLanguage] = useState(() => localStorage.getItem("lg.a11y.lang") || "en-US");

  useEffect(() => {
    document.body.classList.toggle("a11y-large", a11yLarge);
    localStorage.setItem("lg.a11y.large", a11yLarge ? "1" : "0");
  }, [a11yLarge]);
  useEffect(() => {
    document.body.classList.toggle("a11y-contrast", a11yContrast);
    localStorage.setItem("lg.a11y.contrast", a11yContrast ? "1" : "0");
  }, [a11yContrast]);
  useEffect(() => {
    document.body.classList.toggle("reduce-motion", reduceMotion);
    localStorage.setItem("lg.a11y.rm", reduceMotion ? "1" : "0");
  }, [reduceMotion]);
  useEffect(() => {
    localStorage.setItem("lg.a11y.voice", voiceOn ? "1" : "0");
  }, [voiceOn]);
  useEffect(() => {
    localStorage.setItem("lg.a11y.lang", language);
  }, [language]);

  const speak = (text) => {
    if (!voiceOn) return;
    if (!("speechSynthesis" in window)) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = language;
      u.rate = 0.95;
      u.pitch = 1.0;
      window.speechSynthesis.speak(u);
    } catch (e) {
      /* noop */
    }
  };

  const value = useMemo(
    () => ({
      uid,
      a11yLarge,
      setA11yLarge,
      a11yContrast,
      setA11yContrast,
      reduceMotion,
      setReduceMotion,
      voiceOn,
      setVoiceOn,
      language,
      setLanguage,
      speak,
    }),
    [uid, a11yLarge, a11yContrast, reduceMotion, voiceOn, language],
  );

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export function useApp() {
  const v = useContext(AppCtx);
  if (!v) throw new Error("useApp must be used inside AppProvider");
  return v;
}
